import { FloatingActionBar, SearchBox } from '@/components';
import Pagination from '@/components/tables/Pagination';
import { useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeviceList, type DeviceListSort, type DeviceListState } from '../hooks';
import type { Device } from '../types';
import { DevicesTable } from './DevicesTable';

export interface CompanyDevicesSelection {
  selectedIds: Set<string>;
  clearSelection: () => void;
}

interface CompanyDevicesSectionProps {
  companyId: string;
  // Lets the host decide which actions the floating bar offers for the selected devices.
  renderSelectionActions?: (selection: CompanyDevicesSelection) => ReactNode;
  // When omitted the company column stays plain text instead of a link.
  onCompanyClick?: (company: { id: string; name: string }) => void;
  // Passed through to useDeviceList so a host can keep the list state in the URL.
  initialState?: Partial<DeviceListState>;
  onStateChange?: (state: DeviceListState) => void;
  sort?: DeviceListSort;
}

/**
 * Reusable device list for a single company, including every device that is already
 * assigned to one of its daughter companies. The host supplies the selection actions,
 * so the same view serves device management, company details and future screens.
 */
export function CompanyDevicesSection({
  companyId,
  renderSelectionActions,
  onCompanyClick,
  initialState,
  onStateChange,
  sort,
}: CompanyDevicesSectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    searchInput,
    changeSearch,
    page,
    changePage,
    pageSize,
    changePageSize,
    selectedIds,
    setSelectedIds,
    clearSelection,
    devices,
    total,
    isLoading,
    isFetching,
  } = useDeviceList({ companyId, initialState, onStateChange, sort });

  // Open the dedicated device detail page (Overview tab) for the clicked device.
  const handleRowClick = useCallback(
    (device: Device) => {
      navigate({ to: '/devices/$deviceId/overview', params: { deviceId: device.id } });
    },
    [navigate]
  );

  return (
    <div className="space-y-2 w-full grow min-h-0 h-full flex flex-col">
      <SearchBox
        value={searchInput}
        onChange={changeSearch}
        placeholder={t('devices.search.placeholder')}
        ariaLabel={t('devices.search.label')}
        clearAriaLabel={t('devices.search.clear')}
      />

      <div className="relative grow min-h-0 flex flex-col overflow-x-auto">
        {isFetching && !isLoading && (
          <div className="absolute top-2 right-2 z-10">
            <div className="animate-spin size-5 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        )}
        <DevicesTable
          devices={devices}
          isLoading={isLoading}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onRowClick={handleRowClick}
          onCompanyClick={onCompanyClick}
        />
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={changePage}
        onPageSizeChange={changePageSize}
      />

      <FloatingActionBar selectedCount={selectedIds.size}>
        {renderSelectionActions?.({ selectedIds, clearSelection })}
      </FloatingActionBar>
    </div>
  );
}
